import { useEffect, useState } from "react";
import BaseButton from "../../../components/ui/BaseButton";

import BaseModal from "../../../components/ui/BaseModal";

import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import "./style.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCounterOffer,
  deleteOfferExpense,
  fetchUserCounterOffer,
} from "../../../services/api/offers";
import { showToast } from "../../../store/toast/toastSlice";
import useAuth from "../../../hooks/useAuth";
import BaseCard from "../../../components/ui/BaseCard";
import QualificationMatcher from "../components/QualificationMatcher";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseSelect from "../../../components/ui/BaseSelect";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { Avatar, MenuItem } from "@mui/material";
import { fetchWorkOrderPaymetDetails } from "../../../services/api/workOrder";
import { useParams } from "react-router-dom";
import { calculateNewEstimatedFullHalfDayTotal, formatExpenseLabel } from "../../../utils/helpers";
import { getTimeZoneAbbreviation } from "../../../utils/getTimeZoneAbbreviation";
import { CurrencySymbol } from "../../../utils/enum";
import { getFreelanceCompanyMembers } from "../../../services/api/freelanceCompany";

const resetExpense = {
  expenseType: "",
  description: "",
  amount: "",
  addedBy: "FREELANCER",
  isApproved: false,
};

const resetPaymentForm = {
  halfDayRate: "",
  hourlyRate: "",
  totalNewWorkOrderCost: "",
};

const FullDayCounterOfferModal = ({
  counterOfferModalOpen,
  setCounterOfferModalOpen,
  refetchUserAllowedAction,
  mode,
}) => {
  const workOrderDetail = useSelector((state) => state.workOrder.workOrderDetails);
  const dispatch = useDispatch();
  const { authRole, auth } = useAuth();
  const { id } = useParams();
  const isFreelanceCompanyOwner =
    auth?.user?.role?.role === "FREELANCER" &&
    auth?.user?.company?.ownerId === auth?.user?.id;
  const [selectedEngineerId, setSelectedEngineerId] = useState(null);
  const [isAllQualificationsPassed, setIsAllQualificationsPassed] = useState(false);
  const [counterOfferType, setCounterOfferType] = useState("PAY");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [isLabourOpen, setIsLabourOpen] = useState(false);
  const [isExpensesOpen, setIsExpensesOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState(resetExpense);
  const [isEdited, setIsEdited] = useState(false);
  const [paymentForm, setPaymentForm] = useState({});
  const [newPayment, setNewPayment] = useState({});
  const queryClient = useQueryClient();

  const { data: membersData } = useQuery({
    queryKey: ["freelanceCompanyMembers", workOrderDetail?.id],
    queryFn: () =>
      getFreelanceCompanyMembers({
        limit: 100,
        approvedOnly: true,
        workOrderId: workOrderDetail?.id,
      }),
    enabled: !!isFreelanceCompanyOwner,
  });

  const memberOptions = (membersData?.data?.data || []).map((m) => ({
    label: `${m.user.firstName} ${m.user.lastName}`,
    value: m.user.id,
    userId: m.user.id,
    profilePicture: m.user.profilePicture || null,
  }));

  const {
    isLoading,
    error,
    data: workOrderPaymentData,
  } = useQuery({
    queryFn: () => fetchWorkOrderPaymetDetails(id),
    enabled: !!id,
    queryKey: ["workOrderPaymentData"],
    // enabled: false,
  });
  const {
    isLoading: isUserCounterOfferDataLoading,
    error: isUserCounterOfferDataError,
    data: userCounterOfferData,
  } = useQuery({
    queryKey: ["userCounterOfferData"],
    queryFn: () => fetchUserCounterOffer({ id, userId: auth?.user?.id }),
    enabled: !!id && !!auth && mode === "edit",
  });
  const {
    isLoading: isCreateCounterOfferLoading,
    status,
    mutateAsync: createCounterOfferMutation,
  } = useMutation({
    mutationFn: createCounterOffer,
    onSuccess: (data) => {
      dispatch(
        showToast({
          message: "Counter Offer Sent Successfully",
          type: "success",
        }),
      );
    },
  });

  const {
    isLoading: isOfferExpenseLoading,
    error: isOfferExpenseError,
    mutateAsync: deleteOfferExpenseMutation,
  } = useMutation({
    mutationFn: deleteOfferExpense,
    onSuccess: async (data) => {
      queryClient.invalidateQueries(["workOrderPaymentData"]);
      dispatch(
        showToast({
          message: "Expense Deleted Successfully",
          type: "success",
        }),
      );
    },
  });
  useEffect(() => {
    if (mode === "add") return;

    const referredEngineerId = userCounterOfferData?.data?.counter?.referredEngineerId;
    if (referredEngineerId) {
      setSelectedEngineerId(referredEngineerId);
      if (userCounterOfferData?.data?.isEngineerQualificationsVerified) {
        setIsAllQualificationsPassed(true);
      }
    }

    if (userCounterOfferData?.data?.current?.currency) {
      setSelectedCurrency(userCounterOfferData.data.current.currency);
    }

    const userExpenses = userCounterOfferData?.data?.counter?.expenses || [];
    const engineerExpenses = workOrderPaymentData?.data?.engineerRates?.expenses || [];

    const mergedExpenses = [
      ...userExpenses,
      ...engineerExpenses.filter(
        (engExp) => !userExpenses.some((userExp) => userExp.id === engExp.id),
      ),
    ];
    setExpenses(mergedExpenses);

    const totalNewWorkOrderCost = calculateNewEstimatedFullHalfDayTotal(
      mergedExpenses,
      userCounterOfferData?.data?.counter?.fullDayRate,
    );

    setNewPayment({
      fullDayRate: userCounterOfferData?.data?.counter?.fullDayRate,
      hourlyRate: userCounterOfferData?.data?.counter?.hourlyRate,
      totalNewWorkOrderCost,
    });

    setPaymentForm({
      fullDayRate: userCounterOfferData?.data?.counter?.fullDayRate,
      hourlyRate: userCounterOfferData?.data?.counter?.hourlyRate,
    });
  }, [userCounterOfferData, workOrderPaymentData]);

  useEffect(() => {
    if (mode === "edit") return;

    if (workOrderPaymentData?.data?.engineerRates?.currency) {
      setSelectedCurrency(workOrderPaymentData.data.engineerRates.currency);
    }

    setExpenses(
      (workOrderPaymentData?.data?.engineerRates?.expenses || [])?.map((expense) => {
        return {
          ...expense,
          isApproved: true,
        };
      }),
    );
    setPaymentForm({
      fullDayRate: workOrderPaymentData?.data?.engineerRates?.fullDayRate,
      hourlyRate: workOrderPaymentData?.data?.engineerRates?.hourlyRate,
    });
  }, [workOrderPaymentData]);

  const handleCounterOfferButton = async () => {
    if (isFreelanceCompanyOwner && !selectedEngineerId) {
      dispatch(showToast({ message: "Please select an engineer", type: "error" }));
      return;
    }
    if (!isAllQualificationsPassed) return;
    const filteredExpenses = expenses.filter((expense) => expense.addedBy === "FREELANCER");

    const payload = {
      workOrderId: workOrderDetail?.id,
      fullDayRate: newPayment.fullDayRate ?? workOrderPaymentData?.data?.engineerRates?.fullDayRate,
      hourlyRate: newPayment.hourlyRate ?? workOrderPaymentData?.data?.engineerRates?.hourlyRate,
      expenses: filteredExpenses,
      ...(selectedCurrency ? { currency: selectedCurrency } : {}),
      ...(isFreelanceCompanyOwner && selectedEngineerId
        ? { referredEngineerId: selectedEngineerId }
        : {}),
    };

    try {
      await createCounterOfferMutation(payload);
      refetchUserAllowedAction();
      setCounterOfferModalOpen(false);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
        }),
      );
    }
  };

  const handleExpenseChange = (e) => {
    const { name, value } = e.target;
    setNewExpense((prevState) => ({
      ...prevState,
      [name]: name === "amount" ? parseFloat(value) : value,
    }));
  };

  const handleLabourChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prevState) => ({
      ...prevState,
      [name]: parseFloat(value),
    }));
  };

  const handleAddExpenseButton = () => {
    if (
      newExpense.expenseType === "" ||
      newExpense.amount === "" ||
      newExpense.amount === null ||
      isNaN(newExpense.amount)
    ) {
      dispatch(
        showToast({
          message: "Please enter Expense Values",
          type: "error",
        }),
      );
      return;
    }
    const processedExpense = {
      ...newExpense,
      amount: parseFloat(newExpense?.amount),
      isApproved: false,
    };

    const updatedExpenses = [...expenses, processedExpense];

    setExpenses(updatedExpenses);

    const fullDayRate =
      newPayment.fullDayRate || workOrderPaymentData?.data?.engineerRates?.fullDayRate;
    const totalNewWorkOrderCost = calculateNewEstimatedFullHalfDayTotal(
      updatedExpenses,
      parseFloat(fullDayRate?.toFixed(2)),
    );
    setIsEdited(true);
    setNewPayment({
      fullDayRate: newPayment.fullDayRate ?? workOrderPaymentData?.data?.engineerRates?.fullDayRate,
      hourlyRate: newPayment.hourlyRate ?? workOrderPaymentData?.data?.engineerRates?.hourlyRate,
      totalNewWorkOrderCost,
    });

    setNewExpense(resetExpense);
  };

  const handleAddFullDayPay = () => {
    if (
      paymentForm.fullDayRate == "" ||
      paymentForm.fullDayRate === null ||
      isNaN(paymentForm.fullDayRate) ||
      paymentForm.hourlyRate == "" ||
      paymentForm.hourlyRate === null ||
      isNaN(paymentForm.hourlyRate)
    ) {
      dispatch(
        showToast({
          message: "Please enter Labour Values",
          type: "error",
        }),
      );
      return;
    }
    const totalNewWorkOrderCost = calculateNewEstimatedFullHalfDayTotal(
      expenses,
      paymentForm?.fullDayRate,
    );
    setIsEdited(true);
    setNewPayment({ ...paymentForm, totalNewWorkOrderCost });
    setPaymentForm(resetPaymentForm);
  };

  const handleDeleteExpenseButton = async (expenseId) => {
    try {
      const deleteExpenseResponse = await deleteOfferExpenseMutation(expenseId);

      const filteredExpenses = expenses.filter(
        (expense) => expense.id !== deleteExpenseResponse?.data?.id,
      );

      const totalNewWorkOrderCost = calculateNewEstimatedFullHalfDayTotal(
        filteredExpenses,
        paymentForm?.fullDayRate,
      );
      setIsEdited(true);
      setNewPayment({ ...paymentForm, totalNewWorkOrderCost });
      setExpenses(filteredExpenses);
    } catch (error) {}
  };
  const engineerCurrency = selectedCurrency || workOrderPaymentData?.data?.engineerRates?.currency || CurrencySymbol[userCounterOfferData?.data?.current?.currency];
  return (
    <BaseModal open={counterOfferModalOpen} close={() => setCounterOfferModalOpen(false)}>
      <div className="container bg-light xl:px-10">
        <h5 className="text-dark border-bottom mb-3">Counter Offer</h5>
        {isFreelanceCompanyOwner && (
          <BaseCard className="p-4 mt-4" elevation={0}>
            <h6 className="text-dark mb-3">Select Engineer</h6>
            <BaseAutoSelect
              isDefaultField={false}
              label="Engineer"
              labelShrink={false}
              required={true}
              options={memberOptions}
              value={selectedEngineerId || ""}
              onChange={(e) => {
                setSelectedEngineerId(e.target.value);
                setIsAllQualificationsPassed(false);
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    {...optionProps}
                    style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: "12px" }}
                  >
                    <Avatar src={option.profilePicture} sx={{ width: 36, height: 36, fontSize: "14px" }}>
                      {option.label?.charAt(0)}
                    </Avatar>
                    <div>
                      <p style={{ margin: 0, fontSize: "13px", fontWeight: 400, color: "#686868" }}>
                        {option.label}
                      </p>
                      <p style={{ margin: 0, fontSize: "11px", color: "#9e9e9e" }}>
                        ID: {option.userId}
                      </p>
                    </div>
                  </MenuItem>
                );
              }}
              ListboxProps={{ style: { maxHeight: 320 } }}
            />
          </BaseCard>
        )}
        {(!isFreelanceCompanyOwner || selectedEngineerId) && !isAllQualificationsPassed && (
          <BaseCard className="p-4 mt-6" elevation={0}>
            <h6 className="text-dark mb-3">
              WO Qualifications
              {!isAllQualificationsPassed && (
                <>
                  <span>: </span>
                  <span className="text-xs text-red-600">
                    (To proceed with this work order request, please ensure you add the required
                    qualifications)
                  </span>
                </>
              )}
            </h6>
            <QualificationMatcher
              setIsAllQualificationsPassed={setIsAllQualificationsPassed}
              workOrderQualifications={workOrderDetail?.qualificationSubTypes}
              overrideUserId={isFreelanceCompanyOwner ? selectedEngineerId : undefined}
            />
          </BaseCard>
        )}

        <BaseCard className="p-4 mt-6" elevation={0}>
          <div className="flex justify-between border-bottom mb-2">
            <h6 className="text-dark">Schedule</h6>
            <div className="flex items-center bg-[#F2FAFF] py-1 px-4 rounded-md mb-2">
              <i className="fa-solid fa-circle-exclamation text-primary"></i>
              <p className=" ml-3 font-normal">Fixed Start</p>
            </div>
          </div>
          <div className="flex justify-between">
            <p>Start at a specific date and time</p>
            <p>
              {dayjs
                .utc(workOrderDetail.orderStartDate)
                .tz(workOrderDetail.timeZone || "UTC")
                .format("dddd, MM/DD/YYYY")}{" "}
              at{" "}
              {dayjs
                .utc(workOrderDetail.orderStartTime)
                .tz(workOrderDetail.timeZone || "UTC")
                .format("h:mm A")}
              <br />({getTimeZoneAbbreviation(workOrderDetail.timeZone)})
            </p>
          </div>
          <div>
            <h6>Labour</h6>
            <div className="flex items-center mt-4 mb-2">
              <p className="mr-5">Full Day Rate</p>
              <div className="border-t border-dotted border-gray-500 flex-grow"></div>
              {mode === "add" ? (
                <>
                  <p
                    className={`ml-5 font-normal ${newPayment?.fullDayRate ? "line-through" : ""}`}
                  >
                    {engineerCurrency}{" "}
                    {parseFloat(
                      workOrderPaymentData?.data?.engineerRates?.fullDayRate?.toFixed(2),
                    ) || 0}
                  </p>
                  {newPayment?.fullDayRate && (
                    <p className="ml-3 font-normal">
                      {engineerCurrency}{" "}
                      {parseFloat(newPayment?.fullDayRate)?.toFixed(2)}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p
                    className={`ml-5 font-normal ${newPayment?.fullDayRate ? "line-through" : ""}`}
                  >
                    {engineerCurrency}{" "}
                    {parseFloat(userCounterOfferData?.data?.current?.fullDayRate?.toFixed(2)) || 0}
                  </p>
                  {newPayment?.fullDayRate && (
                    <p className="ml-3 font-normal">
                      {engineerCurrency}{" "}
                      {parseFloat(newPayment?.fullDayRate)?.toFixed(2)}
                    </p>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center mb-2">
              <p className="mr-5">Additional Hourly Rate</p>
              <div className="border-t border-dotted border-gray-500  flex-grow"></div>
              <p className={`ml-5 font-normal  ${newPayment?.hourlyRate ? "line-through" : ""}`}>
                {engineerCurrency}{" "}
                {parseFloat(
                  userCounterOfferData?.data
                    ? userCounterOfferData?.data?.current?.hourlyRate?.toFixed(2)
                    : workOrderPaymentData?.data?.engineerRates?.hourlyRate?.toFixed(2),
                ) || 0}
              </p>
              {newPayment?.hourlyRate && (
                <p className="ml-3 font-normal">
                  {" "}
                  {engineerCurrency}{" "}
                  {newPayment?.hourlyRate}
                </p>
              )}
            </div>
          </div>

          <div className="mt-6">
            <h6>Expenses</h6>
            {expenses?.length > 0 &&
              expenses?.map((expense, index) => (
                <div className="flex items-center mt-2 mb-3" key={index}>
                  <p className="mr-5">
                    {formatExpenseLabel(expense)}
                  </p>
                  <div className="border-t border-dotted border-gray-500  flex-grow"></div>
                  <div className="flex items-center bg-light py-1 px-3 rounded-md ml-5">
                    {expense.isApproved ? (
                      <i className="fa-solid fa-check"></i>
                    ) : (
                      <i className="fa-solid fa-xmark"></i>
                    )}
                    <p className="ml-2 font-normal">
                      {expense?.addedBy?.role?.role === "CLIENT" ? "Buyer" : "Provider"}:{" "}
                      {engineerCurrency}{" "}
                      {expense?.amount?.toFixed(2) || 0}
                    </p>
                  </div>
                  {authRole === "FREELANCER" && expense?.addedBy?.role?.role === "FREELANCER" && (
                    <i
                      className="fa-regular fa-trash-can text-red-500 text-base ml-2 cursor-pointer"
                      onClick={() => handleDeleteExpenseButton(expense?.id)}
                    ></i>
                  )}
                </div>
              ))}
          </div>

          <div className="mt-6">
            <div className="flex justify-between bg-primary px-4 py-1 rounded-md">
              <h6 className="!text-white">Minimum Payable</h6>
              <div className="flex">
                {mode === "add" ? (
                  <>
                    <h6
                      className={`!text-white ${
                        newPayment?.totalNewWorkOrderCost > 0 ? "line-through mr-6" : ""
                      }`}
                    >
                      {engineerCurrency}{" "}
                      {workOrderPaymentData?.data?.engineerRates?.totalWorkOrderCost?.toFixed(2)}
                    </h6>

                    {newPayment?.totalNewWorkOrderCost > 0 && (
                      <h6 className="!text-white">
                        {engineerCurrency}{" "}
                        {parseFloat(newPayment?.totalNewWorkOrderCost)?.toFixed(2)}
                      </h6>
                    )}
                  </>
                ) : (
                  <>
                    <h6 className="!text-white line-through mr-6">
                      {engineerCurrency}{" "}
                      {calculateNewEstimatedFullHalfDayTotal(
                        userCounterOfferData?.data?.current?.expenses || [],
                        parseFloat(userCounterOfferData?.data?.current?.fullDayRate?.toFixed(2)),
                      )?.toFixed(2)}
                    </h6>

                    <h6 className="!text-white">
                      {engineerCurrency}{" "}
                      {newPayment?.totalNewWorkOrderCost
                        ? parseFloat(newPayment?.totalNewWorkOrderCost)?.toFixed(2)
                        : calculateNewEstimatedFullHalfDayTotal(
                            expenses,
                            parseFloat(
                              workOrderPaymentData?.data?.engineerRates?.fullDayRate?.toFixed(2),
                            ),
                          )?.toFixed(2)}
                    </h6>
                  </>
                )}
              </div>
            </div>

            <p className="mt-3 text-primary xl:pr-28 text-justify">
              <span className="font-normal">Please note:</span> This is the minimum payable amount;
              however, the final payment will be determined upon job completion based on the actual
              time worked.
            </p>
          </div>
        </BaseCard>

        <BaseCard className="p-4 mt-6" elevation={0}>
          <div>
            <div className="flex items-center gap-2">
              <BaseCheckbox onChange={(e) => setIsLabourOpen(e.target.checked)} />
              <h6 className="text-base font-normal">Labour</h6>
            </div>

            {isLabourOpen && (
              <div>
                {isFreelanceCompanyOwner && (
                  <div className="xl:w-1/4 mb-4">
                    <BaseAutoSelect
                      isDefaultField={false}
                      label="Currency"
                      labelShrink={false}
                      required={true}
                      name="currency"
                      value={workOrderPaymentData?.data?.engineerRates?.currency || selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      options={Object.keys(CurrencySymbol).map((key) => ({ label: key, value: key }))}
                      disabled={!!workOrderPaymentData?.data?.engineerRates?.currency}
                    />
                  </div>
                )}
              <div className="grid grid-cols-7 xl:gap-10 gap-4 items-center">
                <div className="xl:col-span-6 col-span-7 grid grid-cols-3 xl:gap-10 gap-4 items-end xl:mb-2">
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField value="Full Day Rate" isDefaultField={false} disabled={true} />
                  </div>
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField
                      isDefaultField={false}
                      label="Rate"
                      labelShrink={false}
                      optional={false}
                      name="fullDayRate"
                      value={paymentForm?.fullDayRate}
                      onChange={(e) => handleLabourChange(e)}
                      type="number"
                    />
                  </div>
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField
                      isDefaultField={false}
                      label="Additional Hourly Rate"
                      labelShrink={false}
                      optional={false}
                      name="hourlyRate"
                      value={paymentForm?.hourlyRate}
                      type="number"
                      onChange={(e) => handleLabourChange(e)}
                    />
                  </div>
                </div>
                <div className="xl:col-span-1 col-span-7">
                  <BaseButton
                    variant="outlined"
                    color="primary"
                    className="py-[6px] xl:mt-3 hover:bg-primary bg-zinc-200/80"
                    fullWidth={true}
                    onClick={handleAddFullDayPay}
                  >
                    Add
                  </BaseButton>
                </div>
              </div>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <BaseCheckbox onChange={(e) => setIsExpensesOpen(e.target.checked)} />
              <h6 className="text-base font-normal">Expenses</h6>
            </div>

            {isExpensesOpen && (
              <div className="grid grid-cols-7 xl:gap-10 gap-4 items-center">
                <div className="xl:col-span-6 col-span-7 grid grid-cols-3 xl:gap-10 gap-4 items-end xl:mb-2">
                  <div className="xl:col-span-1 col-span-3">
                    <BaseSelect
                      isDefaultField={false}
                      labelShrink={false}
                      className="py-[5px] mb-1.5"
                      label=""
                      required={true}
                      onChange={(e) => handleExpenseChange(e)}
                      options={[
                        { label: "Travel Expense", value: "TRAVEL" },
                        { label: "Tools Expense", value: "TOOLS" },
                        { label: "Material Expense", value: "MATERIAL" },
                        { label: "All Options", value: "ALL" },
                        { label: "Other", value: "OTHER" },
                      ]}
                      name="expenseType"
                      value={newExpense.expenseType}
                    />
                  </div>
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField
                      isDefaultField={false}
                      label="Description"
                      name="description"
                      labelShrink={false}
                      onChange={(e) => handleExpenseChange(e)}
                      optional={false}
                      value={newExpense.description}
                    />
                  </div>
                  <div className="xl:col-span-1 col-span-3">
                    <BaseTextField
                      isDefaultField={false}
                      label="Total Amount"
                      labelShrink={false}
                      optional={false}
                      onChange={(e) => handleExpenseChange(e)}
                      name="amount"
                      type="number"
                      value={newExpense.amount}
                    />
                  </div>
                </div>
                <div className="xl:col-span-1 col-span-7">
                  <BaseButton
                    variant="outlined"
                    color="primary"
                    className="py-[1px] h-10 xl:mt-5 hover:bg-primary bg-zinc-200/80"
                    fullWidth={true}
                    onClick={handleAddExpenseButton}
                  >
                    Add
                  </BaseButton>
                </div>
              </div>
            )}
          </div>
        </BaseCard>
      </div>

      <div
        className="xl:flex grid grid-cols-2 gap-4 xl:justify-center  bg-white py-6 px-4"
        style={{
          boxShadow: "0px 0px 14px 5px rgba(0, 0, 0, 0.07)",
        }}
      >
        <div className="xl:block col-span-2">
          <BaseButton
            fullWidth={true}
            className="py-[4px] mr-8"
            variant="outlined"
            color="primary"
            onClick={() => setCounterOfferModalOpen(false)}
          >
            Cancel
          </BaseButton>
        </div>
        <div className="xl:block col-span-2">
          <BaseButton
            fullWidth={true}
            loaderSize="26px"
            isLoading={isCreateCounterOfferLoading}
            disabled={!isAllQualificationsPassed || !isEdited || (isFreelanceCompanyOwner && !(workOrderPaymentData?.data?.engineerRates?.currency || selectedCurrency))}
            className="py-[5px] xl:w-40"
            color="primary"
            onClick={handleCounterOfferButton}
          >
            Send Counter Offer
          </BaseButton>
        </div>
      </div>
    </BaseModal>
  );
};

export default FullDayCounterOfferModal;
